import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DeleteIcon from "@mui/icons-material/Delete";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import PublicIcon from "@mui/icons-material/Public";
import { Alert, Box, Button, Checkbox, Chip, IconButton, List, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText, Paper, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography, } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { addDays, eachWeekOfInterval, endOfWeek, format, isAfter, isBefore, isSameDay, isWithinInterval, startOfWeek, } from "date-fns";
import Holidays from "date-holidays";
import React from "react";
import { useFormContext } from "react-hook-form";
// Country options for holiday detection
const COUNTRY_OPTIONS = [
    { code: "US", name: "United States" },
    { code: "GB", name: "United Kingdom" },
    { code: "AU", name: "Australia" },
    { code: "CA", name: "Canada" },
    { code: "DE", name: "Germany" },
    { code: "FR", name: "France" },
    { code: "ES", name: "Spain" },
    { code: "IT", name: "Italy" },
    { code: "NL", name: "Netherlands" },
    { code: "IE", name: "Ireland" },
    { code: "NZ", name: "New Zealand" },
    { code: "ZA", name: "South Africa" },
    { code: "BR", name: "Brazil" },
    { code: "AR", name: "Argentina" },
    { code: "MX", name: "Mexico" },
];
export const ExclusionsStep = ({ context }) => {
    const { startDate, endDate, endMode, estimatedEndDate, gameWeekStartDay } = context;
    const defaultCountryCode = "US";
    const { watch, setValue, getValues } = useFormContext();
    // Initialize exclusions defaults if not set
    React.useEffect(() => {
        const current = getValues("exclusions");
        if (!current) {
            setValue("exclusions", {
                excludedDates: [],
                blackoutPeriods: [],
                skipHolidays: true,
                countryCode: defaultCountryCode,
            });
        }
    }, [defaultCountryCode, getValues, setValue]);
    const exclusions = watch("exclusions");
    const excludedDates = exclusions?.excludedDates ?? [];
    const blackoutPeriods = exclusions?.blackoutPeriods ?? [];
    const countryCode = exclusions?.countryCode ?? defaultCountryCode;
    // Helper to update exclusions in form
    const setExcludedDates = (updater) => {
        const newValue = typeof updater === "function" ? updater(excludedDates) : updater;
        setValue("exclusions.excludedDates", newValue);
    };
    const setBlackoutPeriods = (updater) => {
        const newValue = typeof updater === "function" ? updater(blackoutPeriods) : updater;
        setValue("exclusions.blackoutPeriods", newValue);
    };
    const setCountryCode = (code) => {
        setValue("exclusions.countryCode", code);
    };
    // State for adding new items (local only, not persisted until added)
    const [newDate, setNewDate] = React.useState(null);
    const [newDateReason, setNewDateReason] = React.useState("");
    const [newBlackoutStart, setNewBlackoutStart] = React.useState(null);
    const [newBlackoutEnd, setNewBlackoutEnd] = React.useState(null);
    const [newBlackoutReason, setNewBlackoutReason] = React.useState("");
    // Calculate effective end date
    const effectiveEndDate = endMode === "date"
        ? endDate
        : estimatedEndDate || addDays(startDate || new Date(), 180);
    // Detect holidays in the date range
    const detectedHolidays = React.useMemo(() => {
        if (!startDate || !effectiveEndDate)
            return [];
        try {
            const hd = new Holidays(countryCode);
            const holidays = [];
            // Check each year in the range
            const startYear = startDate.getFullYear();
            const endYear = effectiveEndDate.getFullYear();
            for (let year = startYear; year <= endYear; year++) {
                const yearHolidays = hd.getHolidays(year);
                for (const holiday of yearHolidays) {
                    const holidayDate = new Date(holiday.date);
                    if (isWithinInterval(holidayDate, {
                        start: startDate,
                        end: effectiveEndDate,
                    }) &&
                        (holiday.type === "public" || holiday.type === "bank")) {
                        holidays.push({
                            date: holidayDate,
                            name: holiday.name,
                            type: holiday.type,
                        });
                    }
                }
            }
            return holidays.sort((a, b) => a.date.getTime() - b.date.getTime());
        }
        catch {
            return [];
        }
    }, [startDate, effectiveEndDate, countryCode]);
    // Check if a date is already excluded
    const isDateExcluded = (date) => {
        return excludedDates.some((ed) => isSameDay(ed.date, date));
    };
    // Get exclusion for a date
    const getExclusion = (date) => {
        return excludedDates.find((ed) => isSameDay(ed.date, date));
    };
    // Toggle holiday exclusion
    const toggleHolidayExclusion = (holiday) => {
        if (isDateExcluded(holiday.date)) {
            setExcludedDates((prev) => prev.filter((ed) => !isSameDay(ed.date, holiday.date)));
        }
        else {
            setExcludedDates((prev) => [
                ...prev,
                {
                    date: holiday.date,
                    reason: holiday.name,
                    isHoliday: true,
                    wholeWeek: false,
                },
            ]);
        }
    };
    // Toggle whole week for an exclusion
    const toggleWholeWeek = (date, wholeWeek) => {
        setExcludedDates((prev) => prev.map((ed) => (isSameDay(ed.date, date) ? { ...ed, wholeWeek } : ed)));
    };
    // Add all detected holidays
    const addAllHolidays = () => {
        const newExclusions = detectedHolidays
            .filter((h) => !isDateExcluded(h.date))
            .map((h) => ({
            date: h.date,
            reason: h.name,
            isHoliday: true,
            wholeWeek: false,
        }));
        setExcludedDates((prev) => [...prev, ...newExclusions]);
    };
    // Remove all holiday exclusions
    const removeAllHolidays = () => {
        setExcludedDates((prev) => prev.filter((ed) => !ed.isHoliday));
    };
    // Add manual date exclusion
    const addManualDate = () => {
        if (!newDate)
            return;
        if (isDateExcluded(newDate))
            return;
        setExcludedDates((prev) => [
            ...prev,
            {
                date: newDate,
                reason: newDateReason || "Manual exclusion",
                isHoliday: false,
                wholeWeek: false,
            },
        ]);
        setNewDate(null);
        setNewDateReason("");
    };
    // Remove date exclusion
    const removeDate = (date) => {
        setExcludedDates((prev) => prev.filter((ed) => !isSameDay(ed.date, date)));
    };
    // Add blackout period
    const addBlackoutPeriod = () => {
        if (!newBlackoutStart || !newBlackoutEnd)
            return;
        if (!isAfter(newBlackoutEnd, newBlackoutStart))
            return;
        setBlackoutPeriods((prev) => [
            ...prev,
            {
                startDate: newBlackoutStart,
                endDate: newBlackoutEnd,
                reason: newBlackoutReason || "Blackout period",
            },
        ]);
        setNewBlackoutStart(null);
        setNewBlackoutEnd(null);
        setNewBlackoutReason("");
    };
    // Remove blackout period
    const removeBlackoutPeriod = (index) => {
        setBlackoutPeriods((prev) => prev.filter((_, i) => i !== index));
    };
    // Calculate total excluded weeks
    const excludedWeeksCount = React.useMemo(() => {
        if (!startDate || !effectiveEndDate)
            return 0;
        const weeks = eachWeekOfInterval({ start: startDate, end: effectiveEndDate }, { weekStartsOn: gameWeekStartDay });
        return weeks.filter((weekStart) => {
            const weekEnd = addDays(weekStart, 6);
            // Check if any whole-week exclusion covers this week
            const hasWholeWeekExclusion = excludedDates.some((ed) => {
                if (!ed.wholeWeek)
                    return false;
                const edWeekStart = startOfWeek(ed.date, {
                    weekStartsOn: gameWeekStartDay,
                });
                return isSameDay(edWeekStart, weekStart);
            });
            if (hasWholeWeekExclusion)
                return true;
            // Check if any single-date exclusion falls in this week
            const hasExcludedDate = excludedDates.some((ed) => !ed.wholeWeek &&
                isWithinInterval(ed.date, { start: weekStart, end: weekEnd }));
            // Check if week overlaps with any blackout period
            const inBlackout = blackoutPeriods.some((bp) => isWithinInterval(weekStart, {
                start: bp.startDate,
                end: bp.endDate,
            }) ||
                isWithinInterval(weekEnd, { start: bp.startDate, end: bp.endDate }) ||
                (isBefore(weekStart, bp.startDate) && isAfter(weekEnd, bp.endDate)));
            return hasExcludedDate || inBlackout;
        }).length;
    }, [startDate, effectiveEndDate, excludedDates, blackoutPeriods]);
    const hasDateRange = startDate && effectiveEndDate;
    return (_jsx(LocalizationProvider, { dateAdapter: AdapterDateFns, children: _jsxs(Box, { children: [_jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 3 }, children: "Specify dates when matches should not be scheduled." }), !hasDateRange && (_jsx(Alert, { severity: "info", sx: { mb: 3 }, children: "Set a season date range in the previous step to detect holidays." })), _jsxs(Paper, { variant: "outlined", sx: { p: 2, mb: 3 }, children: [_jsxs(Stack, { direction: "row", alignItems: "center", spacing: 1, sx: { mb: 2 }, children: [_jsx(PublicIcon, { color: "primary" }), _jsx(Typography, { variant: "subtitle1", fontWeight: 600, children: "Public Holidays" })] }), _jsxs(Box, { sx: { mb: 2 }, children: [_jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 1 }, children: "Country" }), _jsx(Stack, { direction: "row", spacing: 1, sx: { flexWrap: "wrap", gap: 1 }, children: COUNTRY_OPTIONS.map((country) => (_jsx(Chip, { label: country.name, size: "small", color: countryCode === country.code ? "primary" : "default", variant: countryCode === country.code ? "filled" : "outlined", onClick: () => setCountryCode(country.code) }, country.code))) })] }), hasDateRange && detectedHolidays.length > 0 && (_jsxs(_Fragment, { children: [_jsxs(Stack, { direction: "row", spacing: 1, sx: { mb: 2 }, children: [_jsx(Button, { size: "small", variant: "outlined", onClick: addAllHolidays, children: "Add All" }), _jsx(Button, { size: "small", variant: "outlined", onClick: removeAllHolidays, children: "Remove All" })] }), _jsx(List, { dense: true, disablePadding: true, children: detectedHolidays.map((holiday, index) => {
                                        const exclusion = getExclusion(holiday.date);
                                        const isExcluded = !!exclusion;
                                        const weekStart = startOfWeek(holiday.date, {
                                            weekStartsOn: gameWeekStartDay,
                                        });
                                        const weekEndDate = endOfWeek(holiday.date, {
                                            weekStartsOn: gameWeekStartDay,
                                        });
                                        return (_jsxs(ListItem, { sx: {
                                                bgcolor: isExcluded ? "action.selected" : "transparent",
                                                borderRadius: 1,
                                                mb: 0.5,
                                                flexWrap: "wrap",
                                            }, children: [_jsx(ListItemIcon, { sx: { minWidth: 36 }, children: _jsx(Checkbox, { edge: "start", size: "small", checked: isExcluded, onChange: () => toggleHolidayExclusion(holiday) }) }), _jsx(ListItemText, { primary: holiday.name, secondary: format(holiday.date, "EEEE, MMMM d, yyyy"), sx: { flexGrow: 1, minWidth: 200 } }), isExcluded && (_jsxs(ToggleButtonGroup, { size: "small", exclusive: true, value: exclusion.wholeWeek ? "week" : "date", onChange: (_, val) => {
                                                        if (val)
                                                            toggleWholeWeek(holiday.date, val === "week");
                                                    }, sx: { ml: 1 }, children: [_jsx(ToggleButton, { value: "date", sx: { py: 0.25, px: 1.5 }, children: _jsx(Typography, { variant: "caption", children: "Date only" }) }), _jsx(ToggleButton, { value: "week", sx: { py: 0.25, px: 1.5 }, children: _jsxs(Typography, { variant: "caption", children: ["Week (", format(weekStart, "MMM d"), "\u2013", format(weekEndDate, "d"), ")"] }) })] }))] }, index));
                                    }) })] })), hasDateRange && detectedHolidays.length === 0 && (_jsx(Typography, { variant: "body2", color: "text.secondary", children: "No public holidays found in the selected date range." }))] }), _jsxs(Paper, { variant: "outlined", sx: { p: 2, mb: 3 }, children: [_jsxs(Stack, { direction: "row", alignItems: "center", spacing: 1, sx: { mb: 2 }, children: [_jsx(CalendarMonthIcon, { color: "primary" }), _jsx(Typography, { variant: "subtitle1", fontWeight: 600, children: "Specific Dates" })] }), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 2 }, children: "Add individual dates to skip (e.g., club events, tournaments)." }), _jsxs(Stack, { direction: { xs: "column", sm: "row" }, spacing: 2, sx: { mb: 2 }, children: [_jsx(DatePicker, { label: "Date to exclude", value: newDate, onChange: setNewDate, minDate: startDate || undefined, maxDate: effectiveEndDate || undefined, slotProps: {
                                        textField: { size: "small", sx: { minWidth: 180 } },
                                    } }), _jsx(TextField, { size: "small", label: "Reason (optional)", value: newDateReason, onChange: (e) => setNewDateReason(e.target.value), sx: { flexGrow: 1 } }), _jsx(Button, { variant: "contained", startIcon: _jsx(AddIcon, {}), onClick: addManualDate, disabled: !newDate || isDateExcluded(newDate), children: "Add" })] }), excludedDates.filter((ed) => !ed.isHoliday).length > 0 && (_jsx(List, { dense: true, children: excludedDates
                                .filter((ed) => !ed.isHoliday)
                                .sort((a, b) => a.date.getTime() - b.date.getTime())
                                .map((exclusion, index) => (_jsxs(ListItem, { sx: { bgcolor: "action.hover", borderRadius: 1, mb: 0.5 }, children: [_jsx(ListItemIcon, { sx: { minWidth: 36 }, children: _jsx(EventBusyIcon, { fontSize: "small", color: "error" }) }), _jsx(ListItemText, { primary: format(exclusion.date, "EEEE, MMMM d, yyyy"), secondary: exclusion.reason }), _jsx(ListItemSecondaryAction, { children: _jsx(IconButton, { edge: "end", size: "small", onClick: () => removeDate(exclusion.date), children: _jsx(DeleteIcon, { fontSize: "small" }) }) })] }, index))) }))] }), _jsxs(Paper, { variant: "outlined", sx: { p: 2, mb: 3 }, children: [_jsxs(Stack, { direction: "row", alignItems: "center", spacing: 1, sx: { mb: 2 }, children: [_jsx(EventBusyIcon, { color: "primary" }), _jsx(Typography, { variant: "subtitle1", fontWeight: 600, children: "Blackout Periods" })] }), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 2 }, children: "Add date ranges when no matches should be scheduled (e.g., Christmas break, school holidays)." }), _jsxs(Stack, { direction: { xs: "column", sm: "row" }, spacing: 2, sx: { mb: 2 }, children: [_jsx(DatePicker, { label: "Start date", value: newBlackoutStart, onChange: setNewBlackoutStart, minDate: startDate || undefined, maxDate: effectiveEndDate || undefined, slotProps: {
                                        textField: { size: "small", sx: { minWidth: 160 } },
                                    } }), _jsx(DatePicker, { label: "End date", value: newBlackoutEnd, onChange: setNewBlackoutEnd, minDate: newBlackoutStart || startDate || undefined, maxDate: effectiveEndDate || undefined, slotProps: {
                                        textField: { size: "small", sx: { minWidth: 160 } },
                                    } }), _jsx(TextField, { size: "small", label: "Reason (optional)", value: newBlackoutReason, onChange: (e) => setNewBlackoutReason(e.target.value), sx: { flexGrow: 1 } }), _jsx(Button, { variant: "contained", startIcon: _jsx(AddIcon, {}), onClick: addBlackoutPeriod, disabled: !newBlackoutStart ||
                                        !newBlackoutEnd ||
                                        !isAfter(newBlackoutEnd, newBlackoutStart), children: "Add" })] }), blackoutPeriods.length > 0 && (_jsx(List, { dense: true, children: blackoutPeriods.map((period, index) => (_jsxs(ListItem, { sx: { bgcolor: "error.lighter", borderRadius: 1, mb: 0.5 }, children: [_jsx(ListItemIcon, { sx: { minWidth: 36 }, children: _jsx(EventBusyIcon, { fontSize: "small", color: "error" }) }), _jsx(ListItemText, { primary: `${format(period.startDate, "MMM d")} – ${format(period.endDate, "MMM d, yyyy")}`, secondary: period.reason }), _jsx(ListItemSecondaryAction, { children: _jsx(IconButton, { edge: "end", size: "small", onClick: () => removeBlackoutPeriod(index), children: _jsx(DeleteIcon, { fontSize: "small" }) }) })] }, index))) }))] }), _jsxs(Paper, { variant: "outlined", sx: { p: 2, bgcolor: "action.hover", borderRadius: 2 }, children: [_jsx(Typography, { variant: "subtitle2", color: "text.secondary", sx: { mb: 1, textTransform: "uppercase", letterSpacing: 0.5 }, children: "Exclusions Summary" }), _jsxs(Stack, { direction: "row", spacing: 4, children: [_jsxs(Box, { children: [_jsx(Typography, { variant: "h5", fontWeight: 700, color: "primary", children: excludedDates.filter((ed) => ed.isHoliday).length }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: "holidays" })] }), _jsxs(Box, { children: [_jsx(Typography, { variant: "h5", fontWeight: 700, color: "primary", children: excludedDates.filter((ed) => !ed.isHoliday).length }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: "manual dates" })] }), _jsxs(Box, { children: [_jsx(Typography, { variant: "h5", fontWeight: 700, color: "primary", children: blackoutPeriods.length }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: "blackout periods" })] }), _jsxs(Box, { children: [_jsxs(Typography, { variant: "h5", fontWeight: 700, color: "warning.main", children: ["~", excludedWeeksCount] }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: "weeks affected" })] })] })] })] }) }));
};
